import React, {
  useMemo,
  useCallback,
  useState,
  forwardRef,
  Ref,
  useImperativeHandle,
  useEffect,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { Button, ButtonGroup, Grid, makeStyles, Theme } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { RootState } from 'redux/reducers/type';
import { Auth, AUTHORITIES, Preferences, TimeButton } from 'commons/constants/KeyFlowConstants';
import TimeCode from 'commons/utils/TimeCode';
import { calculateHourToSecond } from 'commons/utils/commonUtil';
import { InputPartialProps, InputPartialRefProps } from 'components/InputPartial/type';
import { useStyles } from 'components/InputPartial';
import { usePreference } from 'hooks';
import PartialModal from '../Modal/PartialModal';
import KeyFlowError from '../../commons/framework/KeyFlowError';
import { NO_PARTIAL_WITHDRAWAL_NAME_ERROR } from '../../commons/constants/KeyFlowMessage';
import useError from '../../hooks/useError';

function InputPartial(
  {
    initPartialTitle,
    onInsertLowresPartial,
    onClearPartial,
    onChangeInTime,
    onChangeOutTime,
    onChangeCurrentTime,
  }: InputPartialProps,
  ref: Ref<InputPartialRefProps>,
) {
  const classes = useStyles();
  const { videoPartialMinTime, videoPartialMaxTime } = usePreference();

  const inputPartialRef = useRef<HTMLInputElement>(null);

  const { inTime, outTime, duration, fps } = useSelector((state: RootState) => state.video);
  const { selectedAsset } = useSelector((state: RootState) => state.asset);

  const { onThrowError } = useError();

  const authorities = localStorage.getItem(Auth.AUTHORITIES) || '';
  const hasAuth = authorities.includes(AUTHORITIES.AUTH_CLEAN_PARTIAL);

  const [partialTitle, setPartialTitle] = useState<string>('');

  const [inTimecodeUtil, setInTimecodeUtil] = useState<TimeCode>(new TimeCode(fps));
  const [outTimecodeUtil, setOutTimecodeUtil] = useState<TimeCode>(new TimeCode(fps));

  const [inHourTime, setInHourTime] = useState<string>('00');
  const [inMinuteTime, setInMinuteTime] = useState<string>('00');
  const [inSecondTime, setInSecondTime] = useState<string>('00');

  const [outHourTime, setOutHourTime] = useState('00');
  const [outMinuteTime, setOutMinuteTime] = useState('00');
  const [outSecondTime, setOutSecondTime] = useState('00');

  const [isOpenPartialModal, setIsOpenPartialModal] = useState<boolean>(false);

  useEffect(() => {
    initPartialTitle && setPartialTitle(initPartialTitle);
  }, []);

  useEffect(() => {
    inTimecodeUtil.setSeconds(inTime);
    console.debug({ inTime: inTimecodeUtil.getSeconds() });
    setInHourTime(String(inTimecodeUtil.getValueOfHours()));
    setInMinuteTime(String(inTimecodeUtil.getValueOfMinutes()));
    setInSecondTime(String(inTimecodeUtil.getValueOfSeconds()));
  }, [inTime]);

  useEffect(() => {
    outTimecodeUtil.setSeconds(outTime);
    console.debug({ outTime: outTimecodeUtil.getSeconds() });
    setOutHourTime(String(outTimecodeUtil.getValueOfHours()));
    setOutMinuteTime(String(outTimecodeUtil.getValueOfMinutes()));
    setOutSecondTime(String(outTimecodeUtil.getValueOfSeconds()));
  }, [outTime]);

  useImperativeHandle(
    ref,
    () => ({
      getPartialTitle: () => partialTitle,
      onChangePartialTitle: (title: string) => {
        setPartialTitle(title);
      },
      onFocusInputPartial: () => {
        inputPartialRef.current?.focus();
      },
    }),
    [partialTitle],
  );

  const onClearInOutTime = useCallback(() => {
    setPartialTitle('');
    onClearPartial();
  }, [duration, onClearPartial]);

  const onChangePartialTitle = useCallback((event: React.ChangeEvent<{ value: string }>) => {
    const { value } = event.currentTarget;
    setPartialTitle(value);
  }, []);

  const onRequestLowresPartial = useCallback(() => {
    if (!partialTitle) {
      onThrowError(new KeyFlowError(NO_PARTIAL_WITHDRAWAL_NAME_ERROR));
      return;
    }

    if (hasAuth && selectedAsset?.filePath.proxy) {
      setIsOpenPartialModal(true);
    } else {
      onInsertLowresPartial(partialTitle);
    }
  }, [partialTitle, onInsertLowresPartial]);

  const onChangeInHourTime = useCallback(event => {
    const { value } = event.currentTarget;
    setInHourTime(value);
  }, []);

  const onChangeInMinuteTime = useCallback(event => {
    const { value } = event.currentTarget;
    setInMinuteTime(value);
  }, []);

  const onChangeInSecondTime = useCallback(event => {
    const { value } = event.currentTarget;
    setInSecondTime(value);
  }, []);

  const onBlurInTime = useCallback(
    event => {
      const hour = Number(inHourTime) || 0;
      const minute = Number(inMinuteTime) || 0;
      const second = Number(inSecondTime) || 0;
      let time = calculateHourToSecond(hour, minute, second);
      time < 0 && (time = 0);
      time >= outTime - videoPartialMinTime && (time = outTime - videoPartialMinTime);

      onChangeInTime(time);
      onChangeCurrentTime(time);
    },
    [
      inHourTime,
      inMinuteTime,
      inSecondTime,
      videoPartialMinTime,
      outTime,
      onChangeInTime,
      onChangeCurrentTime,
    ],
  );

  const onChangeOutHourTime = useCallback(event => {
    const { value } = event.currentTarget;
    setOutHourTime(value);
  }, []);

  const onChangeOutMinuteTime = useCallback(event => {
    const { value } = event.currentTarget;
    setOutMinuteTime(value);
  }, []);

  const onChangeOutSecondTime = useCallback(event => {
    const { value } = event.currentTarget;
    setOutSecondTime(value);
  }, []);

  const onBlurOutTime = useCallback(() => {
    const hour = Number(outHourTime) || 0;
    const minute = Number(outMinuteTime) || 0;
    const second = Number(outSecondTime) || 0;
    let time = calculateHourToSecond(hour, minute, second);

    time >= duration && (time = duration);
    time <= inTime + videoPartialMinTime && (time = inTime + videoPartialMinTime);

    onChangeOutTime(time);
    onChangeCurrentTime(time);
  }, [
    outHourTime,
    outMinuteTime,
    outSecondTime,
    videoPartialMinTime,
    inTime,
    onChangeOutTime,
    onChangeCurrentTime,
  ]);

  const onClickInTimeUpButton = useCallback(
    (event: React.MouseEvent<{ dataset: any }>) => {
      const { time: timeType }: { type: boolean; time: string } = event.currentTarget.dataset;
      let hour = Number(inHourTime) || 0;
      let minute = Number(inMinuteTime) || 0;
      let second = Number(inSecondTime) || 0;

      switch (timeType) {
        case TimeButton.timeType.IN_HOUR: {
          hour += 1;
          break;
        }
        case TimeButton.timeType.IN_MINUTE: {
          minute += 1;
          break;
        }
        case TimeButton.timeType.IN_SECOND: {
          second += 1;
          break;
        }
        default:
          break;
      }

      let time = calculateHourToSecond(hour, minute, second);
      time >= outTime - videoPartialMinTime && (time = outTime - videoPartialMinTime);

      onChangeInTime(time);
      onChangeCurrentTime(time);
    },
    [
      videoPartialMinTime,
      outTime,
      inHourTime,
      inMinuteTime,
      inSecondTime,
      onChangeInTime,
      onChangeCurrentTime,
    ],
  );

  const onClickInTimeDownButton = useCallback(
    (event: React.MouseEvent<{ dataset: any }>) => {
      const { time: timeType }: { time: string } = event.currentTarget.dataset;
      let hour = Number(inHourTime) || 0;
      let minute = Number(inMinuteTime) || 0;
      let second = Number(inSecondTime) || 0;

      switch (timeType) {
        case TimeButton.timeType.IN_HOUR: {
          hour -= 1;
          break;
        }
        case TimeButton.timeType.IN_MINUTE: {
          minute -= 1;
          break;
        }
        case TimeButton.timeType.IN_SECOND: {
          second -= 1;
          break;
        }
        default:
          break;
      }

      let time = calculateHourToSecond(hour, minute, second);
      time < 0 && (time = 0);

      onChangeInTime(time);
      onChangeCurrentTime(time);
    },
    [outTime, inHourTime, inMinuteTime, inSecondTime, onChangeInTime, onChangeCurrentTime],
  );

  const onClickOutTimeUpButton = useCallback(
    (event: React.MouseEvent<{ dataset: any }>) => {
      const { time: timeType }: { time: string } = event.currentTarget.dataset;
      let hour = Number(outHourTime) || 0;
      let minute = Number(outMinuteTime) || 0;
      let second = Number(outSecondTime) || 0;

      switch (timeType) {
        case TimeButton.timeType.OUT_HOUR: {
          hour += 1;
          break;
        }
        case TimeButton.timeType.OUT_MINUTE: {
          minute += 1;
          break;
        }
        case TimeButton.timeType.OUT_SECOND: {
          second += 1;
          break;
        }
        default:
          break;
      }

      let time = calculateHourToSecond(hour, minute, second);
      time >= duration && (time = duration);

      onChangeOutTime(time);
      onChangeCurrentTime(time);
    },
    [duration, outHourTime, outMinuteTime, outSecondTime, onChangeOutTime, onChangeCurrentTime],
  );

  const onClickOutTimeDownButton = useCallback(
    (event: React.MouseEvent<{ dataset: any }>) => {
      const { time: timeType }: { time: string } = event.currentTarget.dataset;
      let hour = Number(outHourTime) || 0;
      let minute = Number(outMinuteTime) || 0;
      let second = Number(outSecondTime) || 0;

      switch (timeType) {
        case TimeButton.timeType.OUT_HOUR: {
          hour -= 1;
          break;
        }
        case TimeButton.timeType.OUT_MINUTE: {
          minute -= 1;
          break;
        }
        case TimeButton.timeType.OUT_SECOND: {
          second -= 1;
          break;
        }
        default:
          break;
      }

      let time = calculateHourToSecond(hour, minute, second);
      time <= inTime + Number(videoPartialMinTime) && (time = inTime + Number(videoPartialMinTime));

      onChangeOutTime(time);
      onChangeCurrentTime(time);
    },
    [
      videoPartialMinTime,
      inTime,
      outHourTime,
      outMinuteTime,
      outSecondTime,
      onChangeOutTime,
      onChangeCurrentTime,
    ],
  );

  /**
   * {@link src/components/VideoPlayer/VideoPlayer.tsx} 에서
   * 설정한 keydown(단축키) 이벤트와 중복 되는 버그로 인해 추가
   */
  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    event.nativeEvent.stopImmediatePropagation();
  }, []);

  return (
    <Grid container className={classes.root} onKeyDown={onKeyDown}>
      <PartialModal
        open={isOpenPartialModal}
        close={() => setIsOpenPartialModal(false)}
        handleClickPgm={() => {
          onInsertLowresPartial(partialTitle);
          setIsOpenPartialModal(false);
        }}
        handleClickClean={() => {
          onInsertLowresPartial(partialTitle, true);
          setIsOpenPartialModal(false);
        }}
        isOverTimeLimit={outTime - inTime > 60}
      />
      <Grid item xs={12} className={classes.inputTitleContainer}>
        <input
          ref={inputPartialRef}
          className={classes.inputPartialTitle}
          type="text"
          placeholder="부분인출을 입력해주세요."
          value={partialTitle}
          onChange={onChangePartialTitle}
        />
      </Grid>
      <Grid item xs={12} lg={9} className={classes.inputTimeContainer}>
        <div className={clsx(classes.timeGroupContainer)}>
          <button className={clsx(classes.timeButton)}>시작점</button>
          <div>
            <input
              className={clsx(classes.inputTime)}
              type="number"
              value={inHourTime}
              onChange={onChangeInHourTime}
              onBlur={onBlurInTime}
            />
            <div className={classes.upDownButtonContainer}>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.IN_HOUR}
                onClick={onClickInTimeUpButton}
              >
                <KeyboardArrowUpIcon />
              </span>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.IN_HOUR}
                onClick={onClickInTimeDownButton}
              >
                <KeyboardArrowDownIcon />
              </span>
            </div>
          </div>
          <div>
            <input
              className={clsx(classes.inputTime)}
              type="number"
              value={inMinuteTime}
              onChange={onChangeInMinuteTime}
              onBlur={onBlurInTime}
            />
            <div className={classes.upDownButtonContainer}>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.IN_MINUTE}
                onClick={onClickInTimeUpButton}
              >
                <KeyboardArrowUpIcon />
              </span>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.IN_MINUTE}
                onClick={onClickInTimeDownButton}
              >
                <KeyboardArrowDownIcon />
              </span>
            </div>
          </div>
          <div>
            <input
              className={clsx(classes.inputTime)}
              type="number"
              value={inSecondTime}
              onChange={onChangeInSecondTime}
              onBlur={onBlurInTime}
            />
            <div className={classes.upDownButtonContainer}>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.IN_SECOND}
                onClick={onClickInTimeUpButton}
              >
                <KeyboardArrowUpIcon />
              </span>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.IN_SECOND}
                onClick={onClickInTimeDownButton}
              >
                <KeyboardArrowDownIcon />
              </span>
            </div>
          </div>
        </div>
        <div className={clsx(classes.timeGroupContainer, classes.right)}>
          <button className={clsx(classes.timeButton)}>끝점</button>
          <div>
            <input
              className={clsx(classes.inputTime)}
              type="number"
              value={outHourTime}
              onChange={onChangeOutHourTime}
              onBlur={onBlurOutTime}
            />
            <div className={classes.upDownButtonContainer}>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.OUT_HOUR}
                onClick={onClickOutTimeUpButton}
              >
                <KeyboardArrowUpIcon />
              </span>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.OUT_HOUR}
                onClick={onClickOutTimeDownButton}
              >
                <KeyboardArrowDownIcon />
              </span>
            </div>
          </div>
          <div>
            <input
              className={clsx(classes.inputTime)}
              type="number"
              value={outMinuteTime}
              onChange={onChangeOutMinuteTime}
              onBlur={onBlurOutTime}
            />
            <div className={classes.upDownButtonContainer}>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.OUT_MINUTE}
                onClick={onClickOutTimeUpButton}
              >
                <KeyboardArrowUpIcon />
              </span>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.OUT_MINUTE}
                onClick={onClickOutTimeDownButton}
              >
                <KeyboardArrowDownIcon />
              </span>
            </div>
          </div>
          <div>
            <input
              className={clsx(classes.inputTime)}
              type="number"
              value={outSecondTime}
              onChange={onChangeOutSecondTime}
              onBlur={onBlurOutTime}
            />
            <div className={classes.upDownButtonContainer}>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.OUT_SECOND}
                onClick={onClickOutTimeUpButton}
              >
                <KeyboardArrowUpIcon />
              </span>
              <span
                className={classes.upDownIcon}
                data-time={TimeButton.timeType.OUT_SECOND}
                onClick={onClickOutTimeDownButton}
              >
                <KeyboardArrowDownIcon />
              </span>
            </div>
          </div>
        </div>
      </Grid>
      <Grid item xs={12} lg={3}>
        <ButtonGroup fullWidth className={clsx(classes.buttonContainer)}>
          <Button onClick={onClearInOutTime}>초기화</Button>
          <Button onClick={onRequestLowresPartial}>부분인출</Button>
        </ButtonGroup>
      </Grid>
    </Grid>
  );
}

export default forwardRef(InputPartial);
